import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from './store';
import { fetchOrders, type OrderListMeta, type OrderManagementOrder } from '../adminOrderApi';
import { parseOrderLocation } from '../../utils/orderLocationUtils';
import { cancelOrder as cancelOrderApi } from '../orderApi';

export interface OrderBreakupItem {
  item: string;
  price: number;
  quantity: number;
}

export interface Booking {
  latitude?: any;
  longitude?: any;
  address?: string;
  id: string;
  serviceName: string;
  date: string;
  status: 'pending' | 'completed' | 'cancelled' | 'in-progress';
  price: number;
  providerName: string;
  /** From API order_breakup; used for display when present */
  orderBreakup?: OrderBreakupItem[];
  orderRemarks?: string;
  /** For "Book Again" – optional from order API */
  categoryId?: string;
  categoryName?: string;
  serviceId?: string;
  /** Raw API order_status for cancel eligibility */
  rawOrderStatus?: string;
}

export interface BookingState {
  bookings: Booking[];
  loading: boolean;
  error: string | null;
  hasMore: boolean;
}

const initialState: BookingState = {
  bookings: [],
  loading: false,
  error: null,
  hasMore: true,
};

export interface PaginatedBookings {
  bookings: Booking[];
  meta: OrderListMeta;
}

function pickProviderName(b: OrderManagementOrder): string {
  const row = b as Record<string, unknown>;
  return String(
    row.service_provider_name ??
      row.provider_name ??
      row.providerName ??
      row.serviceProviderName ??
      '',
  ).trim();
}

export function mapOrderToBooking(b: OrderManagementOrder): Booking {
  const breakup: OrderBreakupItem[] = Array.isArray(b.order_breakup)
    ? b.order_breakup.map((x) => ({
        item: x.item ?? '',
        price: Number(x.price) ?? 0,
        quantity: Number(x.quantity) ?? 1,
      }))
    : [];
  const serviceName =
    (typeof b.service_name === 'string' ? b.service_name : undefined) ||
    (breakup.length > 0
      ? breakup.length === 1
        ? breakup[0].item
        : breakup.map((x) => `${x.item}${x.quantity > 1 ? ` × ${x.quantity}` : ''}`).join(', ')
      : '');
  const price = breakup.reduce((sum, x) => sum + x.price * x.quantity, 0);
  const orderStatus = String(b.order_status || 'CREATED').toUpperCase();
  const statusMap: Record<string, Booking['status']> = {
    CREATED: 'pending',
    PENDING: 'pending',
    UNASSIGNED: 'pending',
    ASSIGNED: 'in-progress',
    HOLD: 'pending',
    CONFIRMED: 'in-progress',
    IN_PROGRESS: 'in-progress',
    SP_STARTED: 'in-progress',
    SP_ARRIVED: 'in-progress',
    SP_IN_PROGRESS: 'in-progress',
    SP_FINISHED: 'completed',
    COMPLETED: 'completed',
    FAILED: 'cancelled',
    CANCELLED: 'cancelled',
  };
  const coords = parseOrderLocation(b.order_location);
  return {
    id: b.id || '',
    serviceName,
    date: String(b.createdAt ?? b.created_at ?? ''),
    status: statusMap[orderStatus] ?? 'pending',
    price,
    providerName: pickProviderName(b),
    address: b.order_address || '',
    latitude: coords?.lat,
    longitude: coords?.long,
    orderBreakup: breakup.length > 0 ? breakup : undefined,
    orderRemarks: b.order_remarks,
    categoryId: typeof b.category_id === 'string' ? b.category_id : undefined,
    categoryName: typeof b.category_name === 'string' ? b.category_name : undefined,
    serviceId: b.service_id,
    rawOrderStatus: orderStatus,
  };
}

/** Map dashboard / ongoing-card raw booking into Booking for navigation. */
export function mapAnyToBooking(raw: Record<string, unknown>): Booking {
  return mapOrderToBooking({
    id: String(raw.id ?? raw.order_id ?? ''),
    order_status: String(raw.order_status ?? raw.status ?? 'CREATED'),
    order_address: String(raw.order_address ?? raw.address ?? raw.serviceAddress ?? ''),
    order_breakup: raw.order_breakup as OrderManagementOrder['order_breakup'],
    createdAt: String(raw.createdAt ?? raw.created_at ?? raw.date ?? ''),
    created_at: String(raw.created_at ?? raw.createdAt ?? raw.date ?? ''),
    service_name: String(raw.service_name ?? raw.serviceName ?? raw.name ?? ''),
    order_remarks: String(raw.order_remarks ?? raw.orderRemarks ?? ''),
    service_id: String(raw.service_id ?? raw.serviceId ?? ''),
    category_id: typeof raw.category_id === 'string' ? raw.category_id : undefined,
    category_name: typeof raw.category_name === 'string' ? raw.category_name : undefined,
    service_provider_name: String(
      raw.service_provider_name ??
        raw.provider_name ??
        raw.providerName ??
        raw.serviceProviderName ??
        '',
    ),
    order_location: raw.order_location as OrderManagementOrder['order_location'],
  } as OrderManagementOrder);
}

export const fetchBookingById = createAsyncThunk<
  Booking,
  string,
  { rejectValue: string }
>('bookings/fetchById', async (bookingId, { rejectWithValue }) => {
  try {
    const { fetchOrderById } = await import('../adminOrderApi');
    const order = await fetchOrderById(bookingId);
    if (!order?.id) return rejectWithValue('Booking not found');
    return mapOrderToBooking(order);
  } catch (error: unknown) {
    const err = error as { message?: string };
    return rejectWithValue(err?.message || 'Failed to load booking details');
  }
});

export const fetchBookingsPaginated = createAsyncThunk<
  PaginatedBookings,
  { page: number; pageSize: number; search?: string },
  { rejectValue: string }
>('bookings/fetchPaginated', async ({ page, pageSize, search }, { rejectWithValue }) => {
  try {
    const userId =
      typeof window !== 'undefined' ? localStorage.getItem('user_id') : null;
    const result = await fetchOrders({
      page: page + 1,
      limit: pageSize,
      order_user_id: userId?.trim() || undefined,
      search: search?.trim() || undefined,
    });
    return {
      bookings: result.items.map(mapOrderToBooking),
      meta: result.meta,
    };
  } catch (error: any) {
    return rejectWithValue(error?.response?.data?.message || 'Failed to fetch bookings');
  }
});

export const cancelBookingOrder = createAsyncThunk<
  { orderId: string },
  { orderId: string; reason?: string },
  { rejectValue: string }
>('bookings/cancel', async ({ orderId, reason }, { rejectWithValue }) => {
  try {
    const userId =
      typeof window !== 'undefined' ? localStorage.getItem('user_id') : null;
    if (!userId) {
      return rejectWithValue('Please sign in to cancel this booking.');
    }
    await cancelOrderApi(orderId, { orderUserId: userId, reason });
    return { orderId };
  } catch (error: any) {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to cancel booking';
    return rejectWithValue(msg);
  }
});

const bookingsSlice = createSlice({
  name: 'bookings',
  initialState,
  reducers: {
    resetBookings(state) {
      state.bookings = [];
      state.loading = false;
      state.error = null;
      state.hasMore = true;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBookingsPaginated.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchBookingsPaginated.fulfilled, (state, action: PayloadAction<PaginatedBookings>) => {
        state.loading = false;
        state.hasMore = action.payload.meta.page < action.payload.meta.totalPages;
      })
      .addCase(fetchBookingsPaginated.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || 'Unknown error';
      })
      .addCase(cancelBookingOrder.fulfilled, (state, action) => {
        const booking = state.bookings.find((b) => b.id === action.payload.orderId);
        if (booking) {
          booking.status = 'cancelled';
          booking.rawOrderStatus = 'CANCELLED';
        }
      });
  },
});

export const { resetBookings } = bookingsSlice.actions;
export default bookingsSlice.reducer;

// Selectors
export const selectBookings = (state: RootState) => state.bookings.bookings;
export const selectBookingsLoading = (state: RootState) => state.bookings.loading;
export const selectBookingsError = (state: RootState) => state.bookings.error;
export const selectHasMoreBookings = (state: RootState) => state.bookings.hasMore;
